import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Play, Plus, Trash2, Pencil, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function ExportProfilesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/tenant/export-profiles");
      setItems(data.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this profile? Runs already downloaded will still work.")) return;
    try {
      await api.delete(`/tenant/export-profiles/${id}`);
      toast.success("Profile deleted");
      load();
    } catch (e) { toast.error("Delete failed"); }
  };

  return (
    <div className="space-y-6" data-testid="export-profiles-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs font-medium uppercase tracking-widest text-muted-foreground">
            <Link to="/app/admin/exports" className="hover:underline">Exports</Link> / Profiles
          </div>
          <h1 className="mt-1 font-display text-3xl font-semibold text-primary">Export profiles</h1>
          <p className="mt-2 max-w-2xl text-muted-foreground">
            Saveable export shapes — filters, grain, columns, filetype — all in one bookmarkable profile.
          </p>
        </div>
        <Button asChild data-testid="new-profile-btn">
          <Link to="/app/admin/exports/profiles/new"><Plus className="h-3.5 w-3.5" /> New profile</Link>
        </Button>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">All profiles ({items.length})</CardTitle></CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Grain</TableHead>
                <TableHead>Filetype</TableHead>
                <TableHead>Columns</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={5} className="py-8 text-center">
                  <Loader2 className="mx-auto h-4 w-4 animate-spin" />
                </TableCell></TableRow>
              )}
              {!loading && items.length === 0 && (
                <TableRow><TableCell colSpan={5} className="py-10 text-center text-muted-foreground">
                  No profiles yet.
                </TableCell></TableRow>
              )}
              {items.map((p) => (
                <TableRow key={p.id} data-testid={`profile-row-${p.id}`}>
                  <TableCell>
                    <div className="font-medium">{p.name}</div>
                    {p.description && <div className="text-xs text-muted-foreground">{p.description}</div>}
                  </TableCell>
                  <TableCell><Badge variant="outline">{p.grain}</Badge></TableCell>
                  <TableCell><Badge variant="outline">{p.filetype}</Badge></TableCell>
                  <TableCell className="tabular-nums">{p.columns?.length || 0}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button asChild size="sm" data-testid={`profile-run-${p.id}`}>
                        <Link to={`/app/admin/exports/run?profile=${p.id}`}><Play className="h-3 w-3" /> Run</Link>
                      </Button>
                      <Button asChild size="sm" variant="ghost">
                        <Link to={`/app/admin/exports/profiles/${p.id}`}><Pencil className="h-3 w-3" /></Link>
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => del(p.id)}
                              data-testid={`profile-delete-${p.id}`}>
                        <Trash2 className="h-3 w-3 text-rose-600" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
